import { ImageSource, Sprite, Texture } from "pixi.js";

/**
 * Texturas generadas con Blender (ver `tools/blender/make_textures.py`).
 *
 * Todas son luz, no color: se multiplican sobre lo que el juego ya pinta en
 * vectorial. Por eso valen para todas las skins de campo sin tocar ninguna, y
 * por eso pesan lo que pesan (64 KB las cuatro). La excepcion es el balon
 * (26 KB): ese si es color, porque el balon es el mismo en todos los campos,
 * y la luz se la sigue poniendo `sphere` por encima.
 *
 * La carga es opcional a proposito. El juego nacio sin un solo asset y tiene
 * que seguir arrancando sin ellos: si un PNG falta o el fetch falla, esto deja
 * la cache a null y cada capa se dibuja como antes. Un adorno no puede tumbar
 * un partido.
 */
export interface GameTextures {
  /** Grano de cesped, sin costura. Multiplicar sobre la hierba. */
  turf: Texture;
  /** Malla de red, sin costura, con alfa. Va encima del hueco de porteria. */
  net: Texture;
  /** Esfera iluminada desde arriba. Multiplicar sobre el balon. */
  sphere: Texture;
  /** Sombra de contacto blanda, negra sobre transparente. */
  shadow: Texture;
  /**
   * El balon clasico rodando, en una rejilla de fotogramas (ver
   * `tools/blender/make_ball.py`). Es albedo: la luz la pone `sphere`.
   */
  ball: Texture;
}

const FILES = {
  turf: "textures/turf.png",
  net: "textures/net.png",
  sphere: "textures/sphere.png",
  shadow: "textures/shadow.png",
  ball: "textures/ball.png",
} as const;

let cache: GameTextures | null = null;
let attempted = false;

/** Las texturas ya cargadas, o null si no estan (y entonces no se dibujan). */
export function getTextures(): GameTextures | null {
  return cache;
}

/**
 * Carga las texturas. Se llama una vez al arrancar, antes del primer
 * partido, porque `createField` es sincrono: si llegaran tarde, el campo ya
 * estaria construido sin ellas.
 */
export async function preloadTextures(): Promise<void> {
  if (attempted) return;
  attempted = true;

  const base = import.meta.env.BASE_URL || "/";
  const url = (file: string): string => `${base.replace(/\/$/, "")}/${file}`;

  /**
   * Se carga con `Image` del navegador y no con `Assets` de Pixi a proposito.
   * `Assets` arrastra su resolvedor, su cache y sus parsers: medido, unos
   * 20 kB en crudo por cuatro PNG que ya sabemos donde estan. `decode()`
   * ademas espera a que el bitmap este listo de verdad, no solo descargado.
   */
  const fromImage = async (key: keyof typeof FILES): Promise<Texture> => {
    const img = new Image();
    img.src = url(FILES[key]);
    await img.decode();
    // El balon es el unico que se pinta MAS PEQUENO que su textura (fotogramas
    // de 64 px para una pelota de ~28 px de dispositivo), y es blanco y negro
    // puro: sin mipmaps, el filtro lineal se salta texeles y las costuras
    // parpadean al cambiar de fotograma. 256x256 es potencia de dos, asi que
    // WebGL1 tambien los genera.
    const mipmaps = key === "ball";
    const source = new ImageSource({ resource: img, autoGenerateMipmaps: mipmaps });
    // `repeat` es obligatorio para el relleno en mosaico: sin el, la tesela
    // sale con el borde estirado en vez de repetida. La rejilla del balon no
    // se repite: ahi repetir solo serviria para que el borde de un fotograma
    // se mezclara con el del lado opuesto.
    source.addressMode = key === "ball" ? "clamp-to-edge" : "repeat";
    source.scaleMode = "linear";
    if (mipmaps) source.mipmapFilter = "linear";
    return new Texture({ source });
  };

  try {
    const loaded = await Promise.all(
      (Object.keys(FILES) as Array<keyof typeof FILES>).map(
        async (key) => [key, await fromImage(key)] as const,
      ),
    );
    cache = Object.fromEntries(loaded) as unknown as GameTextures;
  } catch (err) {
    console.warn("[textures] no se pudieron cargar, se sigue en vectorial", err);
    cache = null;
  }
}

/**
 * Cuanto del tile de sombra ocupa el nucleo oscuro. El resto es penumbra, que
 * se sale del radio pedido a proposito: una sombra que acaba justo en el borde
 * del objeto vuelve a tener filo, que es justo lo que se venia a quitar.
 */
const SHADOW_CORE = 0.55;

/**
 * Sombra blanda con el mismo tamano visible que la elipse que sustituye.
 * Devuelve null si no hay texturas, y entonces quien llama dibuja la elipse
 * de siempre.
 */
export function createSoftShadow(radiusX: number, radiusY: number): Sprite | null {
  const tex = getTextures();
  if (!tex) return null;
  const shadow = new Sprite(tex.shadow);
  shadow.anchor.set(0.5);
  shadow.width = (radiusX * 2) / SHADOW_CORE;
  shadow.height = (radiusY * 2) / SHADOW_CORE;
  return shadow;
}
